"use client";

import { useState } from "react";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { normalizeNumberInput, parseIntegerInput } from "@/lib/parse-number";

/** Optional sign, whole digits (may be empty: ".5"), optional point and fraction digits. */
const DECIMAL_RE = /^[+-]?(\d*)(?:\.(\d*))?$/;
const TRAILING_ZEROS_RE = /0+$/;
const MAX_DECIMAL_DIGITS = 10;

function gcd(a: number, b: number): number {
  let x = Math.abs(a);
  let y = Math.abs(b);
  while (y !== 0) {
    const t = y;
    y = x % y;
    x = t;
  }
  return x;
}

type Conversion =
  | { ok: true; headline: string; steps: string[] }
  | { ok: false; message: string };

type Strings = { [K in keyof typeof DECIMAL_TO_FRACTION_STRINGS]: string };

function simplifySteps(
  num: number,
  den: number,
  strings: Strings
): { num: number; den: number; steps: string[] } {
  const g = gcd(num, den);
  if (g <= 1) {
    return {
      num,
      den,
      steps: [t(strings.stepAlreadyLowestTerms, { num, den })],
    };
  }
  return {
    num: num / g,
    den: den / g,
    steps: [
      t(strings.stepGcd, { num, den, g }),
      `(${num} ÷ ${g})/(${den} ÷ ${g}) = ${num / g}/${den / g}`,
    ],
  };
}

function buildFractionResult(
  input: string,
  sign: string,
  whole: number,
  fracDigits: string,
  strings: Strings,
  locale: string
): Conversion {
  const den = 10 ** fracDigits.length;
  const num = Number(fracDigits);
  const steps = [
    tPlural(strings.stepDecimalPart, locale, fracDigits.length, {
      digits: fracDigits,
      num,
      den,
    }),
  ];
  const simplified = simplifySteps(num, den, strings);
  steps.push(...simplified.steps);
  if (whole === 0) {
    return {
      ok: true,
      headline: `${input} = ${sign}${simplified.num}/${simplified.den}`,
      steps,
    };
  }
  const improper = whole * simplified.den + simplified.num;
  steps.push(
    t(strings.stepAddWholePart, {
      whole,
      num: simplified.num,
      den: simplified.den,
      improper,
    })
  );
  return {
    ok: true,
    headline: `${input} = ${sign}${whole} ${simplified.num}/${simplified.den} = ${sign}${improper}/${simplified.den}`,
    steps,
  };
}

export function convertDecimal(
  raw: string,
  strings: typeof DECIMAL_TO_FRACTION_STRINGS,
  locale = "en"
): Conversion {
  const input = raw.trim();
  const normalized = normalizeNumberInput(input);
  const match = normalized === null ? null : DECIMAL_RE.exec(normalized);
  if (normalized === null || !match || (match[1] === "" && !match[2])) {
    return {
      ok: false,
      message: strings.enterDecimalError,
    };
  }
  const sign = normalized.startsWith("-") ? "−" : "";
  const whole = Number(match[1] === "" ? "0" : match[1]);
  const fracDigits = (match[2] ?? "").replace(TRAILING_ZEROS_RE, "");
  if (fracDigits.length > MAX_DECIMAL_DIGITS) {
    return {
      ok: false,
      message: tPlural(strings.pleaseUseAtMost, locale, MAX_DECIMAL_DIGITS),
    };
  }
  if (fracDigits.length === 0) {
    return {
      ok: true,
      headline: `${input} = ${sign}${whole}/1`,
      steps: [t(strings.stepWholeNumber, { input, sign, whole })],
    };
  }
  return buildFractionResult(input, sign, whole, fracDigits, strings, locale);
}

function DecimalToFractionSection({
  strings,
}: {
  strings: typeof DECIMAL_TO_FRACTION_STRINGS;
}) {
  const [decimal, setDecimal] = useState("0.375");
  const locale = useToolLocale();
  const result = convertDecimal(decimal, strings, locale);

  return (
    <section className="space-y-4 rounded-lg border border-border bg-muted/30 p-6">
      <h2 className="font-semibold text-foreground text-lg">
        {strings.decimalToFraction}
      </h2>
      <div className="space-y-2">
        <label
          className="block font-medium text-foreground text-sm"
          htmlFor="dtf-decimal"
        >
          {strings.decimal}
        </label>
        <input
          className="w-40 rounded-lg border border-border bg-card px-3 py-2 text-center text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="dtf-decimal"
          onChange={(e) => setDecimal(e.target.value)}
          placeholder="0.375"
          type="text"
          value={decimal}
        />
      </div>
      {result.ok && (
        <div className="space-y-2">
          <p className="font-semibold text-2xl text-foreground">
            {result.headline}
          </p>
          <div className="space-y-1 rounded-lg border border-border bg-card p-3 font-mono text-muted-foreground text-sm leading-relaxed">
            {result.steps.map((line) => (
              <p key={line}>{line}</p>
            ))}
          </div>
        </div>
      )}
      {!result.ok && (
        <p className="text-muted-foreground text-sm">{result.message}</p>
      )}
      <p className="text-muted-foreground text-sm">{strings.honestNote}</p>
    </section>
  );
}

function FractionToDecimalSection({
  strings,
}: {
  strings: typeof DECIMAL_TO_FRACTION_STRINGS;
}) {
  const [num, setNum] = useState("5");
  const [den, setDen] = useState("8");
  const formatNum = useToolNumber();
  const n = parseIntegerInput(num);
  const d = parseIntegerInput(den);
  const valid = n !== null && d !== null && d !== 0;
  const value = valid ? n / d : 0;
  const rounded = Math.round(value * 1e10) / 1e10;

  return (
    <section className="space-y-4 rounded-lg border border-border bg-muted/30 p-6">
      <h2 className="font-semibold text-foreground text-lg">
        {strings.fractionToDecimal}
      </h2>
      <div className="flex flex-wrap items-end gap-6">
        <div className="space-y-2">
          <label
            className="block font-medium text-foreground text-sm"
            htmlFor="ftd-num"
          >
            {strings.numerator}
          </label>
          <input
            className="w-28 rounded-lg border border-border bg-card px-3 py-2 text-center text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            id="ftd-num"
            onChange={(e) => setNum(e.target.value)}
            type="number"
            value={num}
          />
        </div>
        <div className="space-y-2">
          <label
            className="block font-medium text-foreground text-sm"
            htmlFor="ftd-den"
          >
            {strings.denominator}
          </label>
          <input
            className="w-28 rounded-lg border border-border bg-card px-3 py-2 text-center text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            id="ftd-den"
            onChange={(e) => setDen(e.target.value)}
            type="number"
            value={den}
          />
        </div>
      </div>
      {valid && (
        <div className="space-y-2">
          <p className="font-semibold text-2xl text-foreground">
            {n}/{d} = {formatNum(rounded, { maximumFractionDigits: 10 })}
          </p>
          <p className="rounded-lg border border-border bg-card p-3 font-mono text-muted-foreground text-sm leading-relaxed">
            {n} ÷ {d} = {formatNum(rounded, { maximumFractionDigits: 10 })}{" "}
            {strings.shownToAtMost10DecimalPlaces}
          </p>
        </div>
      )}
      {!valid && (
        <p className="text-muted-foreground text-sm">
          {strings.enterWholeNumbersError}
        </p>
      )}
    </section>
  );
}

/** English strings. A translated content file overrides these through `ui`. */
export const DECIMAL_TO_FRACTION_STRINGS = {
  decimalToFraction: "Decimal → fraction",
  decimal: "Decimal",
  enterDecimalError: "Enter a decimal number like 0.375, 2.75, or -0.5.",
  pleaseUseAtMost:
    "one:Please use at most {count} decimal place.|other:Please use at most {count} decimal places.",
  honestNote:
    "Honest note: a repeating decimal entered truncated (like 0.333 for 0.333…) gives an approximate fraction — 333/1000, not exactly 1/3.",
  fractionToDecimal: "Fraction → decimal",
  numerator: "Numerator",
  denominator: "Denominator",
  shownToAtMost10DecimalPlaces: "(shown to at most 10 decimal places)",
  enterWholeNumbersError:
    "Enter whole numbers for the numerator and denominator. The denominator cannot be zero.",
  stepAlreadyLowestTerms:
    "GCD({num}, {den}) = 1, so {num}/{den} is already in lowest terms",
  stepGcd: "GCD({num}, {den}) = {g}",
  stepDecimalPart:
    "one: The decimal part .{digits} has {count} decimal place, so .{digits} = {num}/{den}|other: The decimal part .{digits} has {count} decimal places, so .{digits} = {num}/{den}",
  stepAddWholePart:
    "Add the whole part: {whole} + {num}/{den} = {whole} {num}/{den} = {improper}/{den}",
  stepWholeNumber:
    "{input} is a whole number, so as a fraction it is {sign}{whole}/1",
} as const;

export default function DecimalToFractionWidget() {
  const strings = useToolUi(DECIMAL_TO_FRACTION_STRINGS);
  return (
    <>
      <DecimalToFractionSection strings={strings} />
      <FractionToDecimalSection strings={strings} />
    </>
  );
}
